import { useMemo } from 'react'
import {
    useFirestoreGeneral,
    useFirestoreUsersApproved,
} from '../../firebase/useFirestore'
import buildStatusHistoryRows from '../../helpers/statusHistory/buildStatusHistoryRows'
import computeTrajectDuration, {
    TRAJECT_END_STATUSES,
    daysBetween,
    trajectStart,
} from '../../helpers/statusHistory/computeTrajectDuration'
import useStatusDisplays from '../../hooks/useStatusDisplay'
import getUserDisplayName from '../../helpers/getUserDisplayName'

const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/**
 * Wat er per soort entiteit anders is: waar het document staat, hoe de
 * statussen heten en waarmee het traject afgelopen is.
 */
const CONFIG = {
    adress: {
        collection: 'Adresses',
        endStatuses: TRAJECT_END_STATUSES.adress,
        emptyText: 'Nog geen statuswisselingen vastgelegd voor dit huishouden.',
    },
    persona: {
        collection: 'Personas',
        endStatuses: TRAJECT_END_STATUSES.persona,
        emptyText: 'Nog geen statuswisselingen vastgelegd voor deze deelnemer.',
    },
}

const dayLabel = (days) => `${days} ${days === 1 ? 'dag' : 'dagen'}`

/**
 * De statuswisselingen van één huishouden of deelnemer, nieuwste bovenaan, met
 * de doorlooptijd van het traject erboven.
 *
 * @param {object} props
 * @param {'adress'|'persona'} props.entityType - Huishouden of deelnemer
 * @param {string} props.entityID - Adresses.ID of Personas.ID (niet de docid)
 * @param {number} [props.now] - Referentiemoment voor 'loopt X dagen'; alleen voor tests
 */
const StatusHistoryCard = ({ entityType, entityID, now }) => {
    const config = CONFIG[entityType]

    // Firestore — hooks staan boven de guard, anders wisselt hun aantal per render.
    // De historie toont de woorden die de organisatie zelf gekozen heeft, ook
    // voor een status die inmiddels verborgen is: wat er ooit gebeurd is
    // verdwijnt niet doordat een status uit beeld gaat.
    const statusLabels = useStatusDisplays()[entityType]?.labels
    const entries = useFirestoreGeneral('StatusHistory', 'EntityID', entityID || '')
    const entity = useFirestoreGeneral(config?.collection || 'Adresses', 'ID', entityID || '')
    const users = useFirestoreUsersApproved()

    const userNames = useMemo(() => {
        const map = {}
        ;(Array.isArray(users) ? users : []).forEach((user) => {
            if (user.ID) map[user.ID] = getUserDisplayName([user])
        })
        return map
    }, [users])

    const rows = useMemo(
        () => buildStatusHistoryRows(entries, { statusLabels, userNames }),
        [entries, statusLabels, userNames]
    )

    // Het aanmaakmoment van het huishouden of de deelnemer is het startpunt voor
    // trajecten die al liepen voordat de historie werd bijgehouden.
    const createdAt = entity?.[0]?.Timestamp
    const createdMs = createdAt && typeof createdAt.toMillis === 'function' ? createdAt.toMillis() : null

    const summary = useMemo(() => {
        if (!config) return ''

        const finished = computeTrajectDuration(entries, {
            endStatuses: config.endStatuses,
            startedAt: createdMs,
        })
        if (finished) return `Doorlooptijd: ${dayLabel(finished.days)}`

        const start = trajectStart(entries, createdMs)
        const running = daysBetween(start, now ?? Date.now())
        if (running === null) return ''

        return `Loopt ${dayLabel(running)}`
    }, [entries, config, createdMs, now])

    if (!config) return null

    return (
        <div className="new-dashboard-card">
            <div className="new-dashboard-card-content">
                <h3 className="new-dashboard-card-title">Statushistorie</h3>

                {summary && <p className="status-history-summary">{summary}</p>}

                {rows.length === 0 ? (
                    <p className="adress-dashboard-empty">{config.emptyText}</p>
                ) : (
                    <ul className="adress-dashboard-list status-history-list">
                        {rows.map((row) => (
                            <li key={row.ID} className="adress-dashboard-list-row">
                                <span className="adress-dashboard-list-title">
                                    {row.isStart ? (
                                        <>Aangemeld als <strong>{row.to}</strong></>
                                    ) : (
                                        <>
                                            {row.from || 'Geen status'}
                                            <span className="status-history-arrow">→</span>
                                            <strong>{row.to}</strong>
                                        </>
                                    )}
                                </span>
                                <span className="adress-dashboard-list-meta">
                                    {row.changedAt
                                        ? new Date(row.changedAt).toLocaleDateString('nl-NL', dateOptions)
                                        : 'Zojuist'}
                                    {row.by ? ` · ${row.by}` : ''}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    )
}

export default StatusHistoryCard
